import React from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Button } from '@uhg-abyss/web/ui/Button';
import { useFormV2 } from '@uhg-abyss/web/hooks/useForm';
import { CLIA_PRACTICES } from '@src/state/tinRegistration/utils';

const SectionStack = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
});

const SectionHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '24px',
});

const Label = styled('p', {
  margin: 0,
  fontWeight: 700,
});

const Value = styled('p', {
  margin: 0,
  wordBreak: 'break-all',
  whiteSpace: 'normal',
});

const DetailGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
});

const OneColumnRow = styled('div', {
  maxWidth: '75%',
});

const TwoColumnRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(240px, 340px))',
  gap: '10px',
  justifyContent: 'start',
});

const LicenseRow = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(180px, 260px))',
  gap: '90px',
  justifyContent: 'start',
});

const getUploadedFileName = (fileValue) => {
  if (!fileValue) return 'No file uploaded';
  if (Array.isArray(fileValue)) {
    const file = fileValue[0];
    return file?.name || 'No file name';
  }
  if (typeof fileValue === 'string') return fileValue;
  if (fileValue?.name) return fileValue.name;
  return 'No file name';
};

export const PracticeInformationCard = ({ onEdit, isPrinting }) => {
  const abyssForm = useFormV2();

  const practiceInfo = abyssForm.getValues('practiceInformation') || {};
  const practiceType = practiceInfo.practiceTypeSelection || '';
  const isClia = CLIA_PRACTICES.includes(practiceType);
  const isAsc = practiceType === 'Ambulatory Surgery Center';

  return (
    <V2Card>
      <SectionHeader>
        <h4>Practice information</h4>
        {!isPrinting && (
          <V2Button
            variant="outline"
            size="$sm"
            onClick={() => onEdit(2)}
            icon={{
              icon: 'edit',
              position: 'leading',
              variant: 'outlined',
            }}
          >
            Edit
          </V2Button>
        )}
      </SectionHeader>
      <SectionStack>
        <TwoColumnRow>
          <DetailGroup>
            <Label>Registration type</Label>
            <Value>{practiceInfo.registrationTypeSelection}</Value>
          </DetailGroup>
          <DetailGroup>
            <Label>Practice type</Label>
            <Value>{practiceType}</Value>
          </DetailGroup>
        </TwoColumnRow>

        {isClia && (
          <TwoColumnRow>
            <DetailGroup>
              <Label>CLIA Identification Number</Label>
              <Value>{practiceInfo.cliaIdentification}</Value>
            </DetailGroup>
            <DetailGroup>
              <Label>CMS 116</Label>
              <Value>{getUploadedFileName(practiceInfo.cliaFile)}</Value>
            </DetailGroup>
          </TwoColumnRow>
        )}

        {isAsc && (
          <OneColumnRow>
            <DetailGroup>
              <Label>Medicare ID</Label>
              <Value>{practiceInfo.medicareId}</Value>
            </DetailGroup>
          </OneColumnRow>
        )}

        <OneColumnRow>
          <DetailGroup>
            <Label>License restrictions</Label>
            <Value>{practiceInfo.restrictionSelection}</Value>
          </DetailGroup>
        </OneColumnRow>

        {practiceInfo.restrictionSelection === 'Yes' && (
          <OneColumnRow>
            <DetailGroup>
              <Label>Additional information</Label>
              <Value>{practiceInfo.restrictionInput}</Value>
            </DetailGroup>
          </OneColumnRow>
        )}

        {practiceInfo.medLicense.map((license, idx) => (
          <LicenseRow key={idx}>
            <DetailGroup>
              <Label>License type</Label>
              <Value>{license.licenseType}</Value>
            </DetailGroup>
            <DetailGroup>
              <Label>License number</Label>
              <Value>{license.licenseNumber}</Value>
            </DetailGroup>
            <DetailGroup>
              <Label>License state</Label>
              <Value>{license.state}</Value>
            </DetailGroup>
          </LicenseRow>
        ))}

        <OneColumnRow>
          <DetailGroup>
            <Label>National Provider Identifier (NPI)</Label>
            <Value>{practiceInfo.npiInformation}</Value>
          </DetailGroup>
        </OneColumnRow>
      </SectionStack>
    </V2Card>
  );
};